import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

import { Helmet } from "react-helmet";
import { toast } from "react-toastify";

import { getUsuarios, getClientes, inviteUsuario } from "../api/admin.api";
import useAuth from "../hooks/useAuth";
import AddAccess from "../components/add-access";
import DeleteAccess from "../components/delete-access";

import "../config.js";
import "./admin-access.css";
import "./pagination.css";

const AdminAccess = (props) => {
  const { logout } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const [usuarios, setUsuarios] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total_count: 0,
  });

  const [filterClienteId, setFilterClienteId] = useState("");
  const [invitingUserId, setInvitingUserId] = useState(null);

  const fetchClientes = async () => {
    try {
      const data = await getClientes({ page: 1, limit: 100 });
      setClientes(data.items ?? []);
    } catch {
      toast.error("Error al cargar la lista de empresas");
    }
  };

  const fetchUsuarios = async () => {
    setLoading(true);
    setError(null);

    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...(filterClienteId ? { cliente_id: filterClienteId } : {}),
      };

      const data = await getUsuarios(params);
      setUsuarios(data.items ?? []);
      setPagination((prev) => ({
        ...prev,
        total_count: data.total_count ?? 0,
      }));
    } catch (err) {
      const msg =
        err?.response?.data?.error?.message ||
        err?.response?.data?.message ||
        "Error al cargar los accesos";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClientes();
  }, []);

  useEffect(() => {
    fetchUsuarios();
  }, [pagination.page, filterClienteId]);

  const handleFilterChange = (clienteId) => {
    setFilterClienteId(clienteId);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const getTotalPages = () =>
    Math.max(1, Math.ceil(pagination.total_count / pagination.limit));

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > getTotalPages()) return;
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const getClienteName = (clienteId) => {
    if (!clienteId) return "Admin";
    const found = clientes.find((c) => c.id === clienteId);
    return found ? found.nombre_empresa : clienteId;
  };

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  const handleAddClick = () => setIsAddModalOpen(true);

  const handleCloseAddModal = () => setIsAddModalOpen(false);

  const handleAddSuccess = () => {
    fetchUsuarios();
    handleCloseAddModal();
  };

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedUsuario, setSelectedUsuario] = useState(null);

  const handleDeleteClick = (usuario) => {
    setSelectedUsuario(usuario);
    setIsDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setSelectedUsuario(null);
  };

  const handleDeleteSuccess = () => {
    fetchUsuarios();
    handleCloseDeleteModal();
  };

  const handleInviteClick = async (usuario) => {
    if (!usuario?.id) return;

    setInvitingUserId(usuario.id);

    try {
      const result = await inviteUsuario(usuario.id);

      const successMessage =
        result?.message || `Invitación enviada a ${usuario.email}`;

      toast.success(successMessage);
    } catch (err) {
      const message =
        err?.response?.data?.error?.message ||
        err?.response?.data?.message ||
        "Error al enviar la invitación";
      toast.error(message);
    } finally {
      setInvitingUserId(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Nunca";
    const d = new Date(dateString);
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, "0");
    const min = String(d.getMinutes()).padStart(2, "0");
    return `${dd}/${mm}/${yyyy} ${hh}:${min}`;
  };

  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  return (
    <>
      <div className="admin-access-container">
        <Helmet>
          <title>BIOLAB</title>
          <meta property="og:title" content="Accesos - BIOLAB Platform" />
          <link rel="canonical" href="https://${BASE_URL}/admin-access" />
        </Helmet>
        <div className="window admin-access-thq-window-elm">
          <header data-thq="thq-navbar" className="admin-access-thq-navbar-elm">
            <div
              data-thq="thq-navbar-nav"
              className="admin-access-thq-desktop-menu-elm"
            >
              <div className="admin-access-thq-top-elm1">
                <div className="admin-access-thq-menus-elm1">
                  <Link
                    to="/admin-analysis"
                    className="admin-access-thq-analysis-elm1 bodystandard"
                  >
                    <img
                      alt="Análisis"
                      src="/menu icons/analysis.svg"
                      className="admin-access-thq-icon-elm10"
                    />
                    <span className="admin-access-thq-label-elm10 caption">
                      Análisis
                    </span>
                  </Link>
                  <Link
                    to="/admin-report"
                    className="admin-access-thq-reports-elm1 bodystandard"
                  >
                    <img
                      alt="Informes"
                      src="/menu icons/reports.svg"
                      className="admin-access-thq-icon-elm11"
                    />
                    <span className="admin-access-thq-label-elm11 caption">
                      Informes
                    </span>
                  </Link>
                  <button
                    type="button"
                    className="admin-access-thq-admin-elm1 bodystandard"
                  >
                    <img
                      alt="Gestión"
                      src="/menu icons/admin.svg"
                      className="admin-access-thq-icon-elm12"
                    />
                    <span className="admin-access-thq-label-elm12 caption">
                      Gestión
                    </span>
                  </button>
                </div>
                <div className="admin-access-thq-submenu-elm1">
                  <Link
                    to="/admin-clients"
                    className="admin-access-thq-clients-elm1 bodystandard"
                  >
                    <img
                      alt="Clientes"
                      src="/menu icons/clients.svg"
                      className="admin-access-thq-icon-elm13"
                    />
                    <span className="admin-access-thq-label-elm13 caption">
                      Clientes
                    </span>
                  </Link>
                  <button
                    type="button"
                    className="admin-access-thq-access-elm1 bodystandard"
                  >
                    <img
                      alt="Accesos"
                      src="/menu icons/access.svg"
                      className="admin-access-thq-icon-elm14"
                    />
                    <span className="admin-access-thq-label-elm14 caption">
                      Accesos
                    </span>
                  </button>
                </div>
              </div>
              <button
                type="button"
                className="admin-access-thq-exit-elm1 bodystandard"
                onClick={logout}
              >
                <img
                  alt="Salir"
                  src="/menu icons/exit.svg"
                  className="admin-access-thq-icon-elm15"
                />
                <span className="admin-access-thq-label-elm15 caption">
                  Salir
                </span>
              </button>
            </div>
            <div
              data-thq="thq-burger-menu"
              className="admin-access-thq-burger-menu-elm"
              onClick={() => setIsMobileMenuOpen(true)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ")
                  setIsMobileMenuOpen(true);
              }}
            >
              <svg viewBox="0 0 1024 1024" className="admin-access-icon1">
                <path d="M128 554.667h768c23.552 0 42.667-19.115 42.667-42.667s-19.115-42.667-42.667-42.667h-768c-23.552 0-42.667 19.115-42.667 42.667s19.115 42.667 42.667 42.667zM128 298.667h768c23.552 0 42.667-19.115 42.667-42.667s-19.115-42.667-42.667-42.667h-768c-23.552 0-42.667 19.115-42.667 42.667s19.115 42.667 42.667 42.667zM128 810.667h768c23.552 0 42.667-19.115 42.667-42.667s-19.115-42.667-42.667-42.667h-768c-23.552 0-42.667 19.115-42.667 42.667s19.115 42.667 42.667 42.667z"></path>
              </svg>
            </div>
            <div
              data-thq="thq-mobile-menu"
              className={`admin-access-thq-mobile-menu-elm${
                isMobileMenuOpen ? " admin-access-thq-mobile-menu-open" : ""
              }`}
            >
              <div className="admin-access-thq-nav-elm">
                <div className="admin-access-thq-content-elm1">
                  <div className="admin-access-thq-header-elm1">
                    <img
                      alt="BIOLAB Logo"
                      src="/biolab-logo-200h.png"
                      className="admin-access-thq-biolablogo-elm1"
                    />
                    <div
                      data-thq="thq-close-menu"
                      className="admin-access-thq-close-menu-elm"
                      onClick={closeMobileMenu}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ")
                          closeMobileMenu();
                      }}
                    >
                      <img
                        alt="image"
                        src="/close.svg"
                        className="admin-access-thq-icon-elm16"
                      />
                    </div>
                  </div>
                  <div className="admin-access-thq-top-elm2">
                    <div className="admin-access-thq-menus-elm2">
                      <Link
                        to="/admin-analysis"
                        className="admin-access-thq-analysis-elm2 bodystandard"
                        onClick={closeMobileMenu}
                      >
                        <img
                          alt="Análisis"
                          src="/menu icons/analysis.svg"
                          className="admin-access-thq-icon-elm17"
                        />
                        <span className="admin-access-thq-label-elm16">
                          Análisis
                        </span>
                      </Link>
                      <Link
                        to="/admin-report"
                        className="admin-access-thq-reports-elm2 bodystandard"
                        onClick={closeMobileMenu}
                      >
                        <img
                          alt="Informes"
                          src="/menu icons/reports.svg"
                          className="admin-access-thq-icon-elm18"
                        />
                        <span className="admin-access-thq-label-elm17">
                          Informes
                        </span>
                      </Link>
                      <button
                        type="button"
                        className="admin-access-thq-admin-elm2 bodystandard"
                      >
                        <img
                          alt="Gestión"
                          src="/menu icons/admin.svg"
                          className="admin-access-thq-icon-elm19"
                        />
                        <span className="admin-access-thq-label-elm18">
                          Gestión
                        </span>
                      </button>
                    </div>
                    <div className="admin-access-thq-submenu-elm2">
                      <Link
                        to="/admin-clients"
                        className="admin-access-thq-clients-elm2 bodystandard"
                        onClick={closeMobileMenu}
                      >
                        <img
                          alt="Clientes"
                          src="/menu icons/clients.svg"
                          className="admin-access-thq-icon-elm20"
                        />
                        <span className="admin-access-thq-label-elm19">
                          Clientes
                        </span>
                      </Link>
                      <button
                        type="button"
                        className="admin-access-thq-access-elm2 bodystandard"
                      >
                        <img
                          alt="Accesos"
                          src="/menu icons/access.svg"
                          className="admin-access-thq-icon-elm21"
                        />
                        <span className="admin-access-thq-label-elm20">
                          Accesos
                        </span>
                      </button>
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  className="admin-access-thq-exit-elm2 bodystandard"
                  onClick={logout}
                >
                  <img
                    alt="Salir"
                    src="/menu icons/exit.svg"
                    className="admin-access-thq-icon-elm22"
                  />
                  <span className="admin-access-thq-label-elm21">Salir</span>
                </button>
              </div>
            </div>
          </header>
          <div className="admin-access-thq-content-elm2">
            <div className="admin-access-thq-title-elm1">
              <h1 className="admin-access-thq-title-elm2">Accesos</h1>
              <img
                alt="BIOLAB Logo"
                src="/biolab-logo-200h.png"
                className="admin-access-thq-biolablogo-elm2"
              />
            </div>
            <div className="admin-access-thq-content-elm3">
              <button
                type="button"
                className="admin-access-thq-add-access-elm"
                onClick={handleAddClick}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <line x1="19" y1="8" x2="19" y2="14" />
                  <line x1="22" y1="11" x2="16" y2="11" />
                </svg>
                <span>Añadir acceso</span>
              </button>

              <div className="admin-access-thq-table-elm">
                <div className="admin-access-thq-header-elm2">
                  <span className="admin-access-thq-text-elm10">
                    ID Cliente
                  </span>
                  <span className="admin-access-thq-text-elm10">
                    Correo electrónico
                  </span>
                  <span className="admin-access-thq-text-elm12">Invitar</span>
                  <span className="admin-access-thq-text-elm13">Eliminar</span>
                </div>

                {loading && (
                  <div className="admin-access-thq-row1-elm1">
                    <span className="bodysmall">Cargando...</span>
                  </div>
                )}

                {!loading && error && (
                  <div className="admin-access-thq-row1-elm1">
                    <span className="bodysmall">{error}</span>
                  </div>
                )}

                {!loading && !error && usuarios.length === 0 && (
                  <div className="admin-access-thq-row1-elm1">
                    <span className="bodysmall">
                      No hay usuarios registrados
                    </span>
                  </div>
                )}

                {!loading &&
                  !error &&
                  usuarios.map((usuario) => (
                    <div
                      key={usuario.id}
                      className="admin-access-thq-row1-elm1"
                    >
                      <span className="admin-access-thq-text-elm15 bodysmall">
                        {usuario.cliente_id ?? "Admin"}
                      </span>
                      <span className="admin-access-thq-text-elm15 bodysmall">
                        {usuario.email}
                      </span>

                      <div className="admin-access-thq-actions-elm10">
                        <div
                          className="admin-access-thq-invite-elm10"
                          style={{
                            cursor:
                              invitingUserId === usuario.id
                                ? "default"
                                : "pointer",
                            opacity: invitingUserId === usuario.id ? 0.5 : 1,
                          }}
                          onClick={() =>
                            invitingUserId !== usuario.id &&
                            handleInviteClick(usuario)
                          }
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (
                              (e.key === "Enter" || e.key === " ") &&
                              invitingUserId !== usuario.id
                            ) {
                              handleInviteClick(usuario);
                            }
                          }}
                          title={
                            invitingUserId === usuario.id
                              ? "Enviando invitación..."
                              : "Enviar invitación"
                          }
                        >
                          <img
                            alt="Invitar"
                            src="/mail.svg"
                            className="admin-access-thq-icon-elm23"
                          />
                        </div>
                      </div>

                      <div className="admin-access-thq-actions-elm10">
                        <div
                          className="admin-access-thq-delete-elm10"
                          style={{ cursor: "pointer" }}
                          onClick={() => handleDeleteClick(usuario)}
                        >
                          <img
                            alt="Eliminar"
                            src="/delete.svg"
                            className="admin-access-thq-icon-elm24"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
              </div>

              <div className="admin-access-thq-table-mobile-elm">
                {loading && (
                  <div className="admin-access-mobile-card">
                    <span className="bodysmall">Cargando...</span>
                  </div>
                )}

                {!loading && error && (
                  <div className="admin-access-mobile-card">
                    <span className="bodysmall">{error}</span>
                  </div>
                )}

                {!loading && !error && usuarios.length === 0 && (
                  <div className="admin-access-mobile-card">
                    <span className="bodysmall">
                      No hay usuarios registrados
                    </span>
                  </div>
                )}

                {!loading &&
                  !error &&
                  usuarios.map((usuario) => (
                    <div key={usuario.id} className="admin-access-mobile-card">
                      <div className="admin-access-mobile-card-header">
                        <div className="admin-access-mobile-card-user">
                          <span className="admin-access-mobile-card-title">
                            {usuario.email}
                          </span>
                          <span className="admin-access-mobile-card-subtitle bodysmall">
                            {usuario.rol === "admin"
                              ? "Administrador"
                              : "Cliente"}
                          </span>
                        </div>

                        <div className="admin-access-mobile-card-actions">
                          <button
                            type="button"
                            className="admin-access-mobile-icon-button"
                            style={{
                              cursor:
                                invitingUserId === usuario.id
                                  ? "default"
                                  : "pointer",
                              opacity: invitingUserId === usuario.id ? 0.5 : 1,
                            }}
                            onClick={() =>
                              invitingUserId !== usuario.id &&
                              handleInviteClick(usuario)
                            }
                            title={
                              invitingUserId === usuario.id
                                ? "Enviando invitación..."
                                : "Enviar invitación"
                            }
                            aria-label={`Enviar invitación a ${usuario.email}`}
                          >
                            <img
                              alt="Invitar"
                              src="/mail.svg"
                              className="admin-access-mobile-icon"
                            />
                          </button>
                          <button
                            type="button"
                            className="admin-access-mobile-icon-button"
                            onClick={() => handleDeleteClick(usuario)}
                            title="Eliminar usuario"
                            aria-label={`Eliminar ${usuario.email}`}
                          >
                            <img
                              alt="Eliminar"
                              src="/delete.svg"
                              className="admin-access-mobile-icon"
                            />
                          </button>
                        </div>
                      </div>

                      <div className="admin-access-mobile-grid">
                        <div className="admin-access-mobile-field">
                          <span className="admin-access-mobile-label bodysmall">
                            Empresa
                          </span>
                          <span className="admin-access-mobile-value bodysmall">
                            {getClienteName(usuario.cliente_id)}
                          </span>
                        </div>
                        <div className="admin-access-mobile-field">
                          <span className="admin-access-mobile-label bodysmall">
                            Estado
                          </span>
                          <span
                            className="bodysmall admin-access-mobile-status"
                            style={{
                              backgroundColor: usuario.activo
                                ? "#D1FAE5"
                                : "#FEE2E2",
                              color: usuario.activo ? "#065F46" : "#991B1B",
                            }}
                          >
                            {usuario.activo ? "Activo" : "Inactivo"}
                          </span>
                        </div>
                        <div className="admin-access-mobile-field admin-access-mobile-field-full">
                          <span className="admin-access-mobile-label bodysmall">
                            Último acceso
                          </span>
                          <span className="admin-access-mobile-value bodysmall">
                            {formatDate(usuario.ultimo_acceso)}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
              <div className="pagination-controls">
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                >
                  Anterior
                </button>
                <span>
                  Página {pagination.page} de {getTotalPages()}
                </span>
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page >= getTotalPages()}
                >
                  Siguiente
                </button>
                <span>Total: {pagination.total_count} usuarios</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <AddAccess
        isOpen={isAddModalOpen}
        onClose={handleCloseAddModal}
        clientes={clientes}
        onSuccess={handleAddSuccess}
      />

      <DeleteAccess
        isOpen={isDeleteModalOpen}
        onClose={handleCloseDeleteModal}
        usuario={selectedUsuario}
        onSuccess={handleDeleteSuccess}
      />
    </>
  );
};

export default AdminAccess;
